import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import LinksSheet from './LinksSheet';
import { useAuth } from '../../../context/AuthContext';
import { useRestaurantContext } from '../../../context/RestaurantContext';

/**
 * StatusBadgePill
 *
 * Compact clickable pill showing Open (green) or Closed (red dot).
 * When open  → white pill, green dot, text "Open"
 * When closed → white pill, pulsing red dot, shows nextOpenLabel or "Closed"
 */
const StatusBadgePill = ({ storeStatus, onClick }) => {
  if (!storeStatus) return null;

  const { isOpen, label, compactNextOpen, isTemporarilyClosed } = storeStatus;

  // Single-line pill text:
  // Open            → label ("Open" / "Open · 24 hrs")
  // Closed manually → "Closed"
  // Closed by schedule with next open → "Tmrw 8 AM" / "Today 8 AM" / "Mon 8 AM"
  // Closed by schedule, no next open → "Closed"
  const pillText = isOpen
    ? label
    : (!isTemporarilyClosed && compactNextOpen)
      ? compactNextOpen
      : 'Closed';

  return (
    <button
      onClick={onClick}
      className="flex items-center gap-1.5 bg-white border border-gray-100/80 rounded-full px-2.5 py-1.5 shadow-md cursor-pointer transition-transform active:scale-95 hover:shadow-lg"
      style={{ backdropFilter: 'blur(8px)' }}
    >
      <div
        className={`w-2 h-2 rounded-full flex-shrink-0 ${
          isOpen ? 'bg-green-500' : 'bg-red-500'
        }`}
      />
      <span className={`text-[10px] font-black leading-none whitespace-nowrap ${
        isOpen ? 'text-green-700' : 'text-red-600'
      }`}>
        {pillText}
      </span>
      <svg
        className="w-2.5 h-2.5 flex-shrink-0 text-gray-400"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={3}
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
      </svg>
    </button>
  );
};

const BusinessHeader = ({ 
  isCollapsed,
  restaurant, 
  theme, 
  onSearch, 
  searchQuery, 
  selfOrderConfig, 
  onJoinQueue, 
  isQueueActive,
  onViewStatus,
  layoutMode,
  onToggleLayout,
  storeStatus,
  onStatusClick,
}) => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { capabilities } = useRestaurantContext();
  const [showLinks, setShowLinks] = useState(false);
  const [isSearchActive, setIsSearchActive] = useState(false);

  const isReservationActive =
    restaurant?.supportsReservation ||
    selfOrderConfig?.reservationManagement?.enabled ||
    false;
  const activeActionCount = (isQueueActive ? 1 : 0) + (isReservationActive ? 1 : 0);
  const isPersistentSearch = activeActionCount === 0;
  const effectivelySearchActive = isSearchActive || isPersistentSearch;

  const name = restaurant?.name || '';
  const bannerUrl = restaurant?.selfOrderBannerImages?.[0] || restaurant?.image_url || restaurant?.imageUrl || '';
  const imageUrl = restaurant?.image_url || restaurant?.imageUrl || '';
  const address = restaurant?.address || '';
  const phone = restaurant?.phone || '';

  const themeColor = theme?.secondary || '#222';
  const themeTextColor = theme?.primary || '#fff';

  // Helper to add transparency to hex colors
  const addAlpha = (hex, alpha) => {
    if (!hex || typeof hex !== 'string' || !hex.startsWith('#')) return hex;
    const opacity = Math.round(alpha * 255).toString(16).padStart(2, '0');
    return `${hex}${opacity}`;
  };

  const hasLinks = phone || restaurant?.website || restaurant?.gmapLink ||
    restaurant?.socialLinks?.instagram || restaurant?.socialLinks?.facebook;

  useEffect(() => {
    if (searchQuery && !isSearchActive) {
      setIsSearchActive(true);
    }
  }, [searchQuery]);

  const handleCloseSearch = () => {
    if (isPersistentSearch) {
      onSearch({ target: { value: '' } });
    } else {
      setIsSearchActive(false);
      onSearch({ target: { value: '' } });
    }
  };



  return (
    <div className="relative w-full bg-white shadow-sm overflow-hidden">
      <AnimatePresence initial={false}>
        {!isCollapsed ? (
          <motion.div
            key="full-header"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="relative"
          >
            {/* Banner image with gradient overlay */}
            <div className="relative w-full h-24 bg-gray-200 overflow-hidden">
              {bannerUrl ? (
                <img src={bannerUrl} alt={`${name} banner`} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-gradient-to-r from-gray-300 to-gray-400" />
              )}

              {/* Top-Right Profile / Account Button — visible if order or payAndOrder is available */}
              {(capabilities?.canOrder || capabilities?.canPayAndOrder) && (
                <button
                  onClick={() => navigate('profile')}
                  className="absolute top-2.5 right-2.5 z-20 w-9 h-9 rounded-full bg-white/95 backdrop-blur-md shadow-md border border-white/80 flex items-center justify-center text-gray-800 active:scale-90 transition-transform cursor-pointer overflow-hidden"
                  title="Profile & Account"
                >
                  {currentUser?.photoURL ? (
                    <img src={currentUser.photoURL} alt="Profile" className="w-full h-full object-cover" />
                  ) : currentUser?.displayName ? (
                    <span className="text-xs font-black">{currentUser.displayName.charAt(0).toUpperCase()}</span>
                  ) : (
                    <Icon name="User" size={17} />
                  )}
                </button>
              )}

              {/* Status badge — bottom right inside banner */}
              {storeStatus && (
                <div className="absolute bottom-2.5 right-2.5 z-20">
                  <StatusBadgePill storeStatus={storeStatus} onClick={onStatusClick} />
                </div>
              )}
            </div>

            {/* Overlaid circular logo — clickable, carries presence dot */}
            <button
              onClick={onStatusClick}
              className="absolute top-16 left-4 h-16 w-16 bg-white rounded-full p-1 shadow-md z-10 active:scale-95 transition-transform"
            >
              <div className="relative w-full h-full rounded-full overflow-hidden">
                {imageUrl ? (
                  <img src={imageUrl} alt={`${name} logo`} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-gray-300 to-gray-400 flex items-center justify-center">
                    <span className="text-white font-bold text-xl">{name.charAt(0)}</span>
                  </div>
                )}
              </div>
              {/* Presence dot — bottom-right of avatar */}
              {storeStatus && (
                <div
                  className={`absolute bottom-0.5 right-0.5 w-4 h-4 rounded-full border-2 border-white z-20 ${
                    storeStatus.isOpen ? 'bg-green-500' : 'bg-red-500'
                  }`}
                />
              )}
            </button>

            {/* Content below banner */}
            <div className="pt-9 px-4 pb-1">
              <div className="flex justify-between items-center">
                <h1 className="text-xl font-bold leading-tight truncate mr-2">{name}</h1>
                <div className="flex items-center gap-1.5 ml-auto shrink-0">
                  
                  {/* Layout Mode Toggle - Hidden when kiosk mode (ordering) is enabled */}
                  {onToggleLayout && !(selfOrderConfig?.enabledModes?.some(m => ['order', 'payAndOrder', 'showCode'].includes(m))) && (
                    <button
                      onClick={onToggleLayout}
                      className="w-8 h-8 rounded-full flex items-center justify-center transition-all active:scale-95"
                      style={{ backgroundColor: `${theme?.primary || '#000'}15`, color: theme?.primary || '#000' }}
                    >
                      <Icon name={layoutMode === 'card' ? 'List' : 'Grid'} size={16} />
                    </button>
                  )}
                  {hasLinks && (
                    <button
                      onClick={() => setShowLinks(true)}
                      className="rounded-full p-2 flex-shrink-0 transition-transform active:scale-90"
                      style={{ backgroundColor: themeColor }}
                    >
                      <Icon name="Link2" size={16} color={themeTextColor} />
                    </button>
                  )}
                </div>
              </div>
              <div className="flex items-center text-[10px] text-gray-400 mt-0.5 gap-3">
                {address && (
                  <div className="flex items-center gap-1">
                    <svg className="w-3 h-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                    </svg>
                    <span className="truncate max-w-[160px]">{address}</span>
                  </div>
                )}
                {phone && (
                  <div className="flex items-center gap-1">
                    <svg className="w-3 h-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                    </svg>
                    <span>{phone}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Animated Action Bar */}
            <div className="px-3 pb-3 mt-2 h-11 relative flex items-center gap-2">
              <AnimatePresence>
                {!isSearchActive && activeActionCount > 0 && (
                  <motion.div 
                    key="action-buttons"
                    className="flex gap-2 flex-grow overflow-hidden"
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20, width: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    {isQueueActive && (
                      <div 
                        className="flex-1 h-10 flex rounded-xl overflow-hidden border transition-all active:scale-[0.98]"
                        style={{ backgroundColor: '#fff', borderColor: addAlpha(themeColor, 0.3) }}
                      >
                        <button
                          onClick={onJoinQueue}
                          className="w-[80%] h-full font-bold text-[10px] flex items-center justify-center border-r whitespace-nowrap active:bg-gray-50 transition-all gap-1.5"
                          style={{ color: themeColor, borderColor: addAlpha(themeColor, 0.1) }}
                        >
                          <Icon name="UserPlus" size={14} />
                          <span className="truncate">Join Queue</span>
                        </button>
                        <button
                          onClick={onViewStatus}
                          className="w-[20%] h-full flex items-center justify-center active:bg-gray-50 transition-all"
                          style={{ color: themeColor }}
                        >
                          <Icon name="ChevronRight" size={16} />
                        </button>
                      </div>
                    )}
                    {isReservationActive && (
                      <div 
                        className="flex-1 h-10 flex rounded-xl overflow-hidden border transition-all active:scale-[0.98]"
                        style={{ backgroundColor: '#fff', borderColor: addAlpha(themeColor, 0.3) }}
                      >
                        <button
                          onClick={() => navigate(`/restaurant/${restaurant?.id || restaurant?._id}/reservation`)}
                          className="w-[80%] h-full font-bold text-[10px] flex items-center justify-center border-r whitespace-nowrap active:bg-gray-50 transition-all gap-1.5"
                          style={{ color: themeColor, borderColor: addAlpha(themeColor, 0.1) }}
                        >
                          <Icon name="Utensils" size={14} />
                          <span className="truncate">Book Table</span>
                        </button>
                        <button
                          onClick={() => navigate(`/restaurant/${restaurant?.id || restaurant?._id}/reservation`)}
                          className="w-[20%] h-full flex items-center justify-center active:bg-gray-50 transition-all"
                          style={{ color: themeColor }}
                        >
                          <Icon name="ChevronRight" size={16} />
                        </button>
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>

              <motion.div 
                layout
                className={`flex items-center bg-gray-100 rounded-xl overflow-hidden shadow-sm transition-colors ${
                  effectivelySearchActive 
                    ? 'absolute left-3 right-3 z-10' 
                    : activeActionCount < 2 
                      ? 'flex-1 relative' 
                      : 'w-11 relative'
                }`}
                style={{ 
                  height: '40px',
                  backgroundColor: effectivelySearchActive ? '#f3f4f6' : '#fff',
                  border: effectivelySearchActive ? 'none' : '1px solid #f3f4f6'
                }}
                 transition={{ ease: 'easeOut', duration: 0.25 }}
              >
                <div 
                  className={`h-10 flex items-center justify-center cursor-pointer flex-shrink-0 ${
                    !effectivelySearchActive && activeActionCount < 2 ? 'w-full gap-2' : 'w-11'
                  }`}
                  onClick={() => !effectivelySearchActive && setIsSearchActive(true)}
                >
                  <Icon name="Search" size={18} color={effectivelySearchActive ? '#9ca3af' : themeColor} />
                  {!effectivelySearchActive && activeActionCount < 2 && (
                    <span className="font-bold text-[10px]" style={{ color: themeColor }}>
                      Search
                    </span>
                  )}
                </div>
                
                <AnimatePresence>
                  {effectivelySearchActive && (
                    <motion.div 
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="flex-grow flex items-center pr-2"
                    >
                      <input
                        autoFocus={!isPersistentSearch}
                        type="text"
                        placeholder="Search menu..."
                        className="flex-grow bg-transparent border-none outline-none text-sm py-2"
                        value={searchQuery}
                        onChange={onSearch}
                      />
                      {(searchQuery || !isPersistentSearch) && (
                        <button 
                          onClick={handleCloseSearch}
                          className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                        >
                          <Icon name="X" size={18} />
                        </button>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="compact-header"
            initial={{ opacity: 0, height: 0, y: -20 }}
            animate={{ opacity: 1, height: 'auto', y: 0 }}
            exit={{ opacity: 0, height: 0, y: -20 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            className="px-4 py-3 flex items-center justify-between"
          >
            <div className="flex flex-col min-w-0 pr-4">
              <h1 className="text-base font-black leading-tight truncate">{name}</h1>
              <div className="flex items-center text-[10px] text-gray-400 mt-0.5 gap-2 overflow-hidden">
                {address && (
                  <div className="flex items-center gap-1 shrink-0">
                    <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                    </svg>
                    <span className="truncate max-w-[120px]">{address}</span>
                  </div>
                )}
                {phone && (
                  <div className="flex items-center gap-1 shrink-0">
                    <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                    </svg>
                    <span>{phone}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Status badge in compact header */}
            {storeStatus && (
              <StatusBadgePill storeStatus={storeStatus} onClick={onStatusClick} />
            )}

            {hasLinks && (
              <button
                onClick={() => setShowLinks(true)}
                className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 border transition-all active:scale-90"
                style={{ borderColor: addAlpha(themeColor, 0.2), color: themeColor }}
              >
                <Icon name="Link2" size={18} />
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <LinksSheet
        isOpen={showLinks}
        restaurant={restaurant}
        theme={theme}
        onClose={() => setShowLinks(false)}
      />
    </div>
  );
};

export default BusinessHeader;
